import { useState } from 'react'

import {
  CloseIcon,
  IconButton,
  Notice as PopcornNotice,
  SmileIcon,
} from '@buffer-mono/popcorn'
import type { Uploader } from '@buffer-mono/uploader'

import { EmojiPicker } from '~publish/components/EmojiPicker'
import { AIAssistantTrigger } from '~publish/legacy/ai/components/Triggers/AIAssistantTrigger'
import {
  resetContentGeneration,
  setContentGenerationSelection,
  setAIAssistantPlacement,
  toggleAIAssistant,
} from '~publish/legacy/ai/state/slice'
import { getCurrentProfile } from '~publish/legacy/composer/composer/components/app/AppStateless'
import ModalActionCreators from '~publish/legacy/composer/composer/shared-components/modal/actionCreators'
import { Service } from '~publish/legacy/constants/services/ServiceDefinitions'
import { useEmojiInsert } from '~publish/legacy/editor/plugins/emoji'
import { LinkShortenerButton } from '~publish/legacy/integrations-bar/components/LinkShortenerButton'
import { selectShouldShowNBMigration } from '~publish/legacy/organizations/selectors'
import { PostTypeStory } from '~publish/legacy/post/constants'
import store, { useAppSelector, useAppDispatch } from '~publish/legacy/store'

import AddThread from './AddThread'
import CharacterHashtagCount from './CharacterHashtagCount'
import { HashTagManagerButton } from '../../../integrations-bar/components/HashTagManagerButton'
import { IntegrationsBar } from '../../../integrations-bar/IntegrationsBar'
import {
  IntegrationsWrapper,
  Emoji,
  LeftWrapper,
  RightWrapper,
  VerticalDivider,
  IconWrapper,
  ShortLinkNoticeWrapper,
} from '../../../integrations-bar/styles'
import { useIntegrationServices } from '../../../integrations-bar/useIntegrationServices'
import { selectPendingUploads } from '../../../uploads/state/selectors'
import ComposerActionCreators from '../action-creators/ComposerActionCreators'
import { handleUploadError } from '../action-creators/handleUpdloadError'
import type { Draft } from '../entities/Draft'
import { useEditorSelection } from '../hooks/useEditorSelection'

export const ComposerIntegrationsBar = ({
  draft,
  uploader,
  composerRootRef,
}: {
  draft: Draft
  uploader: Uploader
  composerRootRef: React.RefObject<HTMLDivElement>
}): JSX.Element => {
  const context = 'publishComposer'
  const dispatch = useAppDispatch()
  const {
    selectedText,
    hasSelection: hasTextSelection,
    latestSelection,
  } = useEditorSelection(draft.editorState, draft.id)
  const uploadsInProgress = useAppSelector((state) =>
    selectPendingUploads(state, uploader.getId()),
  )

  const shouldShowUnsplashButton =
    !!(draft?.service?.name !== Service.Youtube) &&
    !draft.isReelsPost() &&
    !draft.isFacebookReelPost()

  const shouldShowCanvaButton =
    !draft.isVerticalVideoOnlySupported() && !draft.isFacebookReelPost()

  // call to the hook that returns services configuration for the context
  const { services, activeService } = useIntegrationServices({
    context,
    uploader,
    updateType: draft.isStoryPost()
      ? PostTypeStory
      : draft.updateType ?? undefined, // only used for uploadCareOptions to make differences in case of being PostTypeStory
    serviceName: draft.isStoryPost() ? 'instagramStory' : draft.service.name,
    onError: handleUploadError,
    enableCanva: shouldShowCanvaButton,
    enableUnsplash: shouldShowUnsplashButton,
  })

  const remainingImages = draft.getTotalImageUploadsRemaining()
  const isOverTheLimit = uploadsInProgress.length >= remainingImages
  const disableBar = !draft.canAttachMoreMedia() || isOverTheLimit

  const selectedProfile = getCurrentProfile()

  const showLinkShortenerButton =
    draft.service.isShorteningLinksAvailable && draft.canType()

  const [shortLinkMessageDismissed, setShortlinkMessageDismissed] = useState(
    !!localStorage.getItem('short_link_message_dismissed'),
  )

  const hideShortLinkMessage = (): void => {
    localStorage.setItem('short_link_message_dismissed', 'true')
    setShortlinkMessageDismissed(true)
  }

  const onOpenAiAssistant = (): void => {
    const currentSelectedText = latestSelection || selectedText

    // Sets the placement (source) for tracking
    dispatch(setAIAssistantPlacement({ placement: 'publishComposer' }))

    if (selectShouldShowNBMigration(store.getState())) {
      ModalActionCreators.openModal('AIAssistantMPUpgradePlan', {
        ctaButton: currentSelectedText ? 'contextualMenu' : 'integrationsBar',
      })
      return
    }

    // If there's selected text, set it for the AI Assistant
    if (currentSelectedText) {
      dispatch(resetContentGeneration({ placement: 'publishComposer' }))
      dispatch(setContentGenerationSelection(currentSelectedText))
    }

    dispatch(toggleAIAssistant(true))
    ComposerActionCreators.updateSidebarOnTop(null, 'aiassistant')
  }

  const handleEmojiSelect = useEmojiInsert({ editor: draft.editorState })

  return (
    <>
      {!shortLinkMessageDismissed &&
        showLinkShortenerButton &&
        draft.shouldShowShortLinkMessage() && (
          <ShortLinkNoticeWrapper>
            <PopcornNotice
              variant="warning"
              className={'notice-link-too-short'}
            >
              <PopcornNotice.Text>
                This link is too short for further shortening{' '}
                <IconWrapper>
                  <CloseIcon onClick={hideShortLinkMessage} size="small" />
                </IconWrapper>
              </PopcornNotice.Text>
            </PopcornNotice>
          </ShortLinkNoticeWrapper>
        )}
      <IntegrationsWrapper className="boxed">
        <IntegrationsBar
          context={context}
          services={services}
          activeService={activeService}
          disableBar={disableBar}
        >
          <LeftWrapper>
            {draft.canType() && (
              <>
                <VerticalDivider />
                <Emoji data-tip="Add Emoji">
                  <EmojiPicker
                    onEmojiSelect={handleEmojiSelect}
                    emojiPickerRef={composerRootRef}
                  >
                    <IconButton
                      data-testid="emoji-picker-button"
                      aria-label="Select emoji"
                      label="Select emoji"
                      variant="tertiary"
                      tooltip="Select emoji"
                    >
                      <SmileIcon />
                    </IconButton>
                  </EmojiPicker>
                </Emoji>
              </>
            )}
            <HashTagManagerButton draft={draft} />
            {showLinkShortenerButton && selectedProfile && (
              <LinkShortenerButton
                draft={draft}
                selectedProfileId={selectedProfile.id}
                domainForLinkShorteningSelectedProfile={
                  selectedProfile.shorteningDomain
                }
              />
            )}
            {draft.canType() && (
              <>
                <VerticalDivider />
                <AIAssistantTrigger
                  onClick={onOpenAiAssistant}
                  hasSelection={hasTextSelection}
                />
              </>
            )}
          </LeftWrapper>
          <RightWrapper>
            <CharacterHashtagCount draft={draft} />
            <AddThread draft={draft} />
          </RightWrapper>
        </IntegrationsBar>
      </IntegrationsWrapper>
    </>
  )
}
